import React, { useEffect, useState, useRef } from "react";
import { Modal, Form, Input, Button, Row, Checkbox } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { RootState } from "store/store";
import i18n from "i18n";
import {
  splitStringByNewline,
  emailIsInvalid,
  urlIsInvalid,
} from "../../utils/strings";
import {
  updateSurveyMeoSiteSettings,
  uploadMeoSiteLogo,
} from "./MeoSiteSettingsActions";
import {
  SurveyMeoSiteSettingsUpdateItem,
  SurveyMeoSitesEditForm,
  LogoUploadResult,
} from "api/sms-api";
import { connect } from "react-redux";
import { editReviewLink } from "../survey_meo_sites/SurveyMeoSitesActions";
import { showNotification, showErrorNotification } from "utils/notifications";
import { setReviewLinkWarning } from "utils/setReviewLinkWarning";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
];

interface SurveyDetail {
  reviewLink: string;
  surveyMeoSitesId: string;
  surveyTitle: string;
}

interface ReviewLinkMailEditProps {
  visible: boolean;
  closeModal: () => void;
  meoSiteId: number | null;
  meoSiteName: string | null;
  recipientEmail: Array<string> | null;
  logoUrl: string | null;
  isAdminMailDisabled: boolean;
  pageSize: number;
  page: number;
  surveyCount: number;
  surveyDetails: Array<{
    surveyMeoSitesId: string;
    reviewLink: string;
    surveyTitle: string;
  }>;

  updateSurveyMeoSiteSettings: (
    pageSize: number,
    page: number,
    form: Array<SurveyMeoSiteSettingsUpdateItem>
  ) => void;

  editReviewLink: (
    surveyId: string,
    params: SurveyMeoSitesEditForm
  ) => Promise<void>;

  uploadMeoSiteLogo: (file: File) => Promise<LogoUploadResult | undefined>;
  logoUploading: boolean;
}

const ReviewLinkMailEdit: React.FC<ReviewLinkMailEditProps> = (props) => {
  const [form] = Form.useForm();
  const [selectedFile, setSelectedFile] = useState<File | null>(null); // 選択中のファイル（未アップロード）
  const [previewUrl, setPreviewUrl] = useState<string | null>(null); // プレビュー用URL（ローカル or S3）
  const [isSubmitting, setIsSubmitting] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const blobUrlRef = useRef<string | null>(null); // blob URLを追跡してメモリリークを防ぐ

  // blob URLのクリーンアップ関数
  const revokeBlobUrl = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  };

  useEffect(() => {
    form.resetFields();
    if (props.visible) {
      form.setFieldsValue({
        recipientEmail: props.recipientEmail?.join("\n") || null,
        isAdminMailDisabled: props.isAdminMailDisabled,
      });
      revokeBlobUrl(); // モーダルを開く際に以前のblob URLをクリーンアップ
      setPreviewUrl(props.logoUrl); // 既存のS3 URLをプレビューに設定
      setSelectedFile(null);
    }
    // モーダルが閉じる際のクリーンアップ
    return () => {
      revokeBlobUrl();
    };
  }, [
    props.visible,
    form,
    props.recipientEmail,
    props.logoUrl,
    props.isAdminMailDisabled,
  ]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    const file = e.target.files[0];

    // ファイルサイズチェック（10MB）
    if (file.size > MAX_FILE_SIZE) {
      showErrorNotification(i18n.t("meoSiteSettings.logo.fileSizeError"));
      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
      return;
    }

    // ファイル形式チェック
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      showErrorNotification(i18n.t("meoSiteSettings.logo.fileTypeError"));
      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
      return;
    }

    // 以前のblob URLがあればクリーンアップ
    revokeBlobUrl();

    // ファイルを保持し、ローカルプレビューを表示（APIは呼ばない）
    setSelectedFile(file);
    const newBlobUrl = URL.createObjectURL(file);
    blobUrlRef.current = newBlobUrl;
    setPreviewUrl(newBlobUrl);
  };

  const handleLogoRemove = () => {
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
    revokeBlobUrl(); // blob URLをクリーンアップ
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const surveyDetails: SurveyDetail[] = (props.surveyDetails || []).map(
    (detail: any) => ({
      reviewLink: detail.reviewLink,
      surveyMeoSitesId: detail.surveyMeoSitesId,
      surveyTitle: detail.surveyTitle,
    })
  );

  const onClickSetReviewLinkMailButton = async (
    meoSiteId: number | null,
    reviewLinkValues: { reviewLink: string; surveyMeoSitesId: string }[]
  ) => {
    if (meoSiteId !== null && !isSubmitting) {
      setIsSubmitting(true);
      const recipientEmails = form.getFieldValue("recipientEmail");
      const mailArray = splitStringByNewline(recipientEmails);

      // 新しいファイルが選択されている場合のみS3にアップロード
      let finalLogoUrl: string | null = previewUrl; // previewUrl が null ならDBにも null を保存
      if (selectedFile) {
        const result = await props.uploadMeoSiteLogo(selectedFile);
        if (result) {
          finalLogoUrl = result.url;
        } else {
          // アップロード失敗時は処理を中断
          setIsSubmitting(false);
          return;
        }
      }

      const updateMailItems: Array<SurveyMeoSiteSettingsUpdateItem> = [
        {
          meoSiteId: meoSiteId,
          recipientEmail: mailArray,
          logoUrl: finalLogoUrl,
          isAdminMailDisabled:
            form.getFieldValue("isAdminMailDisabled") ?? false,
        },
      ];
      props.updateSurveyMeoSiteSettings(
        props.pageSize,
        props.page,
        updateMailItems
      );

      let success = false;
      const promises = reviewLinkValues.map((reviewLinkValue) => {
        if (reviewLinkValue.surveyMeoSitesId !== "") {
          const updateReviewLinkItems = {
            reviewLink: reviewLinkValue.reviewLink,
          };
          return props
            .editReviewLink(
              reviewLinkValue.surveyMeoSitesId,
              updateReviewLinkItems
            )
            .then(() => {
              success = true;
            });
        } else {
          return Promise.resolve();
        }
      });
      await Promise.all(promises);
      if (success) {
        showNotification(i18n.t("reviewLinks.editSuccess"));
      }
      setReviewLinkWarning();
      form.resetFields();
      revokeBlobUrl(); // blob URLをクリーンアップ
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsSubmitting(false);
      props.closeModal();
    }
  };

  const onClickCancelButton = () => {
    form.resetFields();
    revokeBlobUrl(); // blob URLをクリーンアップ
    setSelectedFile(null);
    setPreviewUrl(null);
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
    props.closeModal();
  };

  return (
    <Modal
      destroyOnClose
      visible={props.visible}
      title={
        i18n.t("meoSiteSettings.table.title.meoSiteName") +
        " ： " +
        props.meoSiteName
      }
      okText={i18n.t("common.save")}
      cancelText={i18n.t("common.cancel")}
      okButtonProps={{ disabled: isSubmitting || props.logoUploading }}
      onCancel={onClickCancelButton}
      onOk={() => {
        form
          .validateFields()
          .then(() => {
            const reviewLinkInputs = document.querySelectorAll(
              'input[name^="reviewLink"]'
            );
            const reviewLinkValues = Array.from(reviewLinkInputs).map(
              (input) => {
                return {
                  reviewLink: (input as HTMLInputElement).value.trim(),
                  surveyMeoSitesId: (input as HTMLTextAreaElement).id,
                };
              }
            );
            onClickSetReviewLinkMailButton(props.meoSiteId, reviewLinkValues);
          })
          .catch(() => {
            showErrorNotification(
              i18n.t("meoSiteSettings.table.title.recipientEmail")
            );
          });
      }}
    >
      <Form form={form} layout="vertical">
        {[
          ...(surveyDetails?.length
            ? surveyDetails
            : [{ surveyMeoSitesId: "", surveyTitle: "", reviewLink: "" }]),
        ].map((detail, index) => (
          <Form.Item
            key={detail.surveyMeoSitesId ?? index}
            name={detail.surveyMeoSitesId ?? `reviewLink${index}`}
            label={
              index === 0 ? (
                <span>
                  <span style={{ fontWeight: "bold" }}>
                    ・{i18n.t("meoSiteSettings.table.title.reviewLink")}
                  </span>
                  <br />
                  {detail.surveyTitle !== "" ? (
                    <>
                      {i18n.t("meoSiteSettings.table.title.surveyName")}：
                      {detail.surveyTitle}
                    </>
                  ) : (
                    <>
                      {i18n.t("meoSiteSettings.table.title.surveyName")}：
                      {i18n.t("survey.table.item.untitled")}
                    </>
                  )}
                </span>
              ) : (
                <span>
                  {detail.surveyTitle !== "" ? (
                    <>
                      {i18n.t("meoSiteSettings.table.title.surveyName")}：
                      {detail.surveyTitle}
                    </>
                  ) : (
                    <>
                      {i18n.t("meoSiteSettings.table.title.surveyName")}：
                      {i18n.t("survey.table.item.untitled")}
                    </>
                  )}
                </span>
              )
            }
            initialValue={detail.reviewLink ?? ""}
            rules={[
              {
                validator(_, value) {
                  const reviewLinkArray = splitStringByNewline(value);
                  if (urlIsInvalid(reviewLinkArray)) {
                    return Promise.reject(new Error(i18n.t("validation.url")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              name={`reviewLink${index}`}
              disabled={surveyDetails?.length === 0}
              placeholder={
                surveyDetails?.length === 0
                  ? i18n.t("meoSiteSettings.table.settings.noSetSurvey")
                  : ""
              }
            />
          </Form.Item>
        ))}
        <Form.Item
          name="recipientEmail"
          label={
            <span style={{ fontWeight: "bold" }}>
              ・{i18n.t("meoSiteSettings.table.title.recipientEmail")}
            </span>
          }
          rules={[
            {
              validator(_, value) {
                const mailArray = splitStringByNewline(value);
                if (emailIsInvalid(mailArray)) {
                  return Promise.reject(new Error(i18n.t("validation.mail")));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input.TextArea
            name="recipientEmail"
            rows={5}
            placeholder={i18n.t(
              "meoSiteSettings.settingMail.inputMailPlaceholder"
            )}
          />
        </Form.Item>
        <Form.Item
          name="isAdminMailDisabled"
          valuePropName="checked"
          extra={i18n.t(
            "meoSiteSettings.settingMail.disableAdminMailNotificationDescription"
          )}
        >
          <Checkbox>
            {i18n.t("meoSiteSettings.settingMail.disableAdminMailNotification")}
          </Checkbox>
        </Form.Item>
        <Form.Item
          name="logo"
          label={
            <span style={{ fontWeight: "bold" }}>
              ・{i18n.t("meoSiteSettings.table.title.logo")}
            </span>
          }
        >
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={handleLogoChange}
            ref={logoInputRef}
            disabled={props.logoUploading}
          />
          {previewUrl && (
            <Row align="top" style={{ marginTop: "1em" }}>
              <img
                style={{ maxWidth: "200px", maxHeight: "100px" }}
                src={previewUrl}
                alt="logo"
              />
              <Button
                shape="circle"
                icon={<CloseOutlined />}
                type="text"
                onClick={handleLogoRemove}
                disabled={props.logoUploading}
              />
            </Row>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    logoUploading: state.meoSiteSettings.logoUploading,
  };
};

const mapDispatchToProps = {
  updateSurveyMeoSiteSettings,
  editReviewLink,
  uploadMeoSiteLogo,
};

export default connect(mapStateToProps, mapDispatchToProps)(ReviewLinkMailEdit);
